import { useMemo } from 'react';
import { useAtomValue } from 'jotai';

import { NOT_APPLICABLE_LABEL } from '@/constants';
import { formatEnumForDisplay } from '@/utils';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';

import {
  PointOfInterest,
  PointOfInterestTemplateField,
  PoiStatus,
  UnitsEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { localiseMeasurement } from '@/utils/unitSystem';

type PaintBlockDataProps = {
  pointOfInterests: PointOfInterest[];
  columns: PointOfInterestTemplateField[];
};

type PoiRowProps = {
  pointOfInterests: PointOfInterest[];
  unitSystem: UnitSystemEnum;
  columns: PointOfInterestTemplateField[];
  getPoiStatusLabel: (status: string) => string;
};

const generateRows = ({
  pointOfInterests,
  unitSystem,
  columns,
  getPoiStatusLabel,
}: PoiRowProps) => {
  return (pointOfInterests ?? [])
    .map((pointOfInterest) => {
      const { name, templateFieldData, accessibility, id, status } = pointOfInterest;

      if (status === PoiStatus.Completed) return;

      const rowData: { [key: string]: string } = {
        'Chase Item': name || NOT_APPLICABLE_LABEL,
        Component: pointOfInterest.assembly?.tagName || '',
        'Accessibility requirement': formatEnumForDisplay(accessibility),
        id,
        WorkPack: pointOfInterest.workpack?.name || '',
        Status: status ? getPoiStatusLabel(status) : '',
      };

      Object.entries(templateFieldData).forEach(([fieldKey, fieldValue]) => {
        if (!fieldKey || !fieldValue) return;
        const fieldUnit = columns.find((item) => item.key === fieldKey)?.unit;
        const fieldName = columns.find((item) => item.key === fieldKey)?.name;
        if (!fieldName) return;
        rowData[fieldName] =
          fieldUnit === UnitsEnum.SquareMeter
            ? localiseMeasurement({
                unitSystem,
                sourceUnit: UnitsEnum.SquareMeter,
                value: Number(fieldValue),
              }) ?? ''
            : String(fieldValue);
      });
      return rowData;
    })
    .filter((row) => !!row);
};

export const usePaintBlockData = ({ pointOfInterests, columns }: PaintBlockDataProps) => {
  const unitSystem = useAtomValue(unitSystemState);
  const { getPoiStatusLabel } = usePoiStatusLabels();

  const rows = useMemo(
    () =>
      generateRows({ pointOfInterests, unitSystem, columns, getPoiStatusLabel }).filter(
        (row) => !!row
      ),
    [pointOfInterests, unitSystem, columns, getPoiStatusLabel]
  );
  return rows;
};
